import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, API, fmtMoney, fmtDate, fmtDateTime, LEGAL_FOOTER } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { VerifiedStamp } from '@/components/VerifiedStamp';
import { Loader2, Download, ShieldAlert, Calendar, Building2, User, Hash, Receipt } from 'lucide-react';
import { motion } from 'framer-motion';

const ISOLOGO = 'https://customer-assets.emergentagent.com/job_budget-gen-3/artifacts/hs5nxkvk_isologo%20cauratech%20fondo%20oscuro.png';

export default function VerifyPage() {
  const { publicId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/verify/${publicId}`);
        setData(r.data);
      } catch {
        setData({ verified: false });
      } finally { setLoading(false); }
    })();
  }, [publicId]);

  if (loading) {
    return (
      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4">
        <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" />
        <p className="mt-3 text-sm text-muted-foreground">Verificando autenticidad…</p>
      </div>
    );
  }

  if (!data || data?.reason === 'not_found' || !data?.quote) {
    return (
      <div className="relative z-10 mx-auto max-w-2xl px-4 py-16">
        <Card className="glass border-[rgba(255,92,122,0.30)]">
          <CardContent className="flex flex-col items-center gap-4 p-10 text-center">
            <ShieldAlert className="h-10 w-10 text-[#FF5C7A]" />
            <h1 className="font-display text-2xl font-semibold">Presupuesto no encontrado</h1>
            <p className="text-sm text-muted-foreground">
              No pudimos validar este QR. El folio puede haber sido eliminado o ser inválido.
              Si crees que se trata de un error, contacta a Cuaratech.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { verified, expired, quote, company, verified_at } = data;
  const pdfUrl = `${API}/public/quotes/${publicId}/pdf?v=${encodeURIComponent(data?.verified_at || '')}`;

  return (
    <div className="relative z-10 min-h-screen">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-[var(--glass-border)]">
        <div className="absolute inset-0 tech-grid opacity-40" />
        <div className="absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(85,117,245,0.20),transparent_60%)]" />
        <div className="relative mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-14">
          <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <img src={company?.isologo_url || ISOLOGO} alt="Cuaratech" className="h-14 w-14 rounded-lg" />
              <div>
                <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Verificación de presupuesto</div>
                <h1 className="font-display text-2xl font-semibold sm:text-3xl">{company?.name || 'Cuaratech'}</h1>
                <div className="font-mono text-xs text-muted-foreground">RIF: {company?.rif}</div>
              </div>
            </div>
            <VerifiedStamp valid={verified} expired={expired} />
          </div>
          <motion.p
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-6 max-w-2xl text-sm text-muted-foreground"
          >
            Este presupuesto fue emitido oficialmente por {company?.name || 'Cuaratech'}.
            La autenticidad se valida mediante firma criptográfica HMAC-SHA256 contra nuestros registros.
          </motion.p>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="glass border-[var(--glass-border)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                <Hash className="h-3.5 w-3.5" /> Folio
              </div>
              <div className="mt-1.5 font-mono text-base font-semibold" data-testid="verify-quote-number">{quote.quote_number}</div>
              <div className="truncate text-[11px] text-muted-foreground">ID: {quote.public_id}</div>
            </CardContent>
          </Card>
          <Card className="glass border-[var(--glass-border)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" /> Emitido / Vence
              </div>
              <div className="mt-1.5 font-mono text-sm">{fmtDate(quote.issue_date)} → {fmtDate(quote.expiry_date)}</div>
              <div className="text-[11px] text-muted-foreground">{quote.validity_days} días de vigencia</div>
            </CardContent>
          </Card>
          <Card className="glass border-[var(--glass-border)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                <User className="h-3.5 w-3.5" /> Cliente
              </div>
              <div className="mt-1.5 truncate font-medium">{quote.client_snapshot?.name}</div>
              {quote.client_snapshot?.rif && <div className="font-mono text-[11px] text-muted-foreground">RIF: {quote.client_snapshot.rif}</div>}
            </CardContent>
          </Card>
          <Card className="glass border-[var(--glass-border)] glow-primary">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                <Receipt className="h-3.5 w-3.5" /> Total
              </div>
              <div className="mt-1.5 font-mono text-xl font-semibold text-[hsl(var(--primary))]" data-testid="verify-total-amount">{fmtMoney(quote.total, quote.currency)}</div>
              <div className="text-[11px] text-muted-foreground">IVA {(quote.tax_rate * 100).toFixed(0)}% incluido</div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="glass border-[var(--glass-border)] lg:col-span-2">
            <CardContent className="p-5">
              <h3 className="font-display text-base font-semibold">Detalle</h3>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-2 py-2 text-left">#</th>
                      <th className="px-2 py-2 text-left">Descripción</th>
                      <th className="px-2 py-2 text-right">Cant.</th>
                      <th className="px-2 py-2 text-right">P. Unit</th>
                      <th className="px-2 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(quote.items || []).map((it, idx) => (
                      <tr key={idx} className="border-t border-[var(--glass-border)]">
                        <td className="px-2 py-2 font-mono">{idx + 1}</td>
                        <td className="px-2 py-2">{it.description}</td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums">{it.quantity}</td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums">{fmtMoney(it.unit_price)}</td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums">{fmtMoney((it.quantity || 0) * (it.unit_price || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex justify-end">
                <div className="w-full max-w-xs space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-mono tabular-nums">{fmtMoney(quote.subtotal)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">IVA ({(quote.tax_rate * 100).toFixed(0)}%)</span>
                    <span className="font-mono tabular-nums">{fmtMoney(quote.tax_amount)}</span>
                  </div>
                  <div className="divider-accent my-1" />
                  <div className="flex items-center justify-between">
                    <span className="font-display text-base font-semibold">TOTAL</span>
                    <span className="font-mono text-xl font-semibold tabular-nums text-[hsl(var(--primary))]">{fmtMoney(quote.total, quote.currency)}</span>
                  </div>
                </div>
              </div>
              {quote.notes && (
                <div className="mt-5 rounded-lg bg-[var(--surface-1)] p-3 text-sm">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Notas</div>
                  <p className="mt-1 whitespace-pre-line">{quote.notes}</p>
                </div>
              )}

              <div className="mt-5 rounded-lg border border-[rgba(85,117,245,0.35)] bg-[rgba(85,117,245,0.08)] p-3 text-xs leading-relaxed text-foreground" data-testid="verify-legal-footer">
                <span className="font-semibold">Condiciones de pago: </span>
                <span className="whitespace-pre-line text-muted-foreground">{quote.payment_terms || data.legal_footer || LEGAL_FOOTER}</span>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card className="glass border-[var(--glass-border)]">
              <CardContent className="p-5">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-[hsl(var(--primary))]" />
                  <h3 className="font-display text-base font-semibold">Emisor</h3>
                </div>
                <div className="mt-2 text-sm font-medium">{company?.name}</div>
                <div className="font-mono text-xs text-muted-foreground">RIF: {company?.rif}</div>
                <div className="mt-2 text-xs text-muted-foreground">{company?.address}</div>
              </CardContent>
            </Card>
            <Card className="glass border-[var(--glass-border)]">
              <CardContent className="p-5">
                <h3 className="font-display text-base font-semibold">Sellado de tiempo</h3>
                <div className="mt-2 text-xs text-muted-foreground">Verificado el</div>
                <div className="font-mono text-sm" data-testid="verify-timestamp">{fmtDateTime(verified_at)}</div>
                <div className="mt-2 text-xs text-muted-foreground">Hash de integridad</div>
                <div className="break-all rounded bg-[var(--surface-1)] p-2 font-mono text-[10px]">{quote.verification_hash?.slice(0, 48)}…</div>
              </CardContent>
            </Card>
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
              <Button className="btn-primary w-full" data-testid="verify-download-pdf-button">
                <Download className="mr-1.5 h-4 w-4" /> Descargar PDF
              </Button>
            </a>
          </div>
        </div>

        <div className="mt-10 text-center text-[11px] text-muted-foreground">
          © {new Date().getFullYear()} {company?.name || 'Cuaratech'} · Documento generado automáticamente · Verificación: <span className="font-mono">{quote.public_id}</span>
        </div>
      </div>
    </div>
  );
}
